import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Textarea } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { TextCasePipe } from '../../pipes/textCase.pipe';

@Component({
    selector: 'app-expandable-textarea',
    standalone: true,
    imports: [CommonModule, FormsModule, Textarea, TooltipModule, TextCasePipe],
    templateUrl: './expandable-textarea.component.html',
    styleUrl: './expandable-textarea.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ExpandableTextareaComponent),
            multi: true
        }
    ]
})
export class ExpandableTextareaComponent implements ControlValueAccessor {
    @ViewChild('textareaElement', { read: ElementRef }) textareaRef!: ElementRef;

    @Input() label: string = '';
    @Input() placeholder: string = 'Enter text...';
    @Input() maxLength: number = 2000;
    @Input() rows: number = 6;
    @Input() showIcon: boolean = false;
    @Input() iconSrc: string = 'icons/star.svg';
    @Input() iconTooltip: string = 'AI Generated';
    @Input() placeholderTitle: string = '';
    @Input() placeholderItems: string[] = [];

    @Output() valueChange = new EventEmitter<string>();

    textAreaVisible: boolean = false;
    charCount: number = 0;

    private _value: string = '';
    private onChange: (value: string) => void = () => { };
    private onTouched: () => void = () => { };

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const clickedInside = this.textareaRef?.nativeElement.contains(event.target);
        if (!clickedInside && this.textAreaVisible) {
            this.hideTextArea();
        }
    }

    get value(): string {
        return this._value;
    }

    set value(val: string) {
        if (val !== this._value) {
            this._value = val;
            this.updateCharCount();
            this.onChange(val);
            this.valueChange.emit(val);
        }
    }

    writeValue(value: string): void {
        if (value !== this._value) {
            this._value = value || '';
            this.updateCharCount();
            if (this._value) {
                this.textAreaVisible = true;
            }
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    showTextArea(event: Event): void {
        event.stopPropagation();
        this.textAreaVisible = true;
    }

    hideTextArea(): void {
        if (!this._value && this.textAreaVisible) {
            this.textAreaVisible = false;
        }
    }

    updateCharCount(): void {
        this.charCount = this._value.length;
    }

    onValueChange(): void {
        this.updateCharCount();
    }

    onInput(event: Event): void {
        const target = event.target as HTMLTextAreaElement;
        if (target.value.length > this.maxLength) {
            target.value = target.value.slice(0, this.maxLength);
            this.value = target.value;
        }
        this.updateCharCount();
    }

    onPaste(): void {
        setTimeout(() => {
            if (this._value.length > this.maxLength) {
                this.value = this._value.slice(0, this.maxLength);
            }
            this.updateCharCount();
        }, 0);
    }

    onCut(): void {
        setTimeout(() => {
            this.updateCharCount();
        }, 0);
    }
}

